import React from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Table } from 'react-bootstrap';
import {
  GetPendingRegisteredOffersListingsDTO,
  PendingRegisteredOffersListing,
} from '@bb/domain/dist/types';
import { SortDirection, Column } from '@app/types';
import { createFormatDateFunction } from 'utils';

import Form801Modal from './Form801Modal';
import NewOffersModal from './NewOffersModal';

import ManageOfferNotificationModal from './ManageOfferNotificationModal';
import AgentChatLink from '../react-common/AgentChatLink';
import { ContactDrawerMode, ContactDrawerType } from '../../contexts';

type ColumnLabels =
  | 'Address'
  | 'Branch'
  | 'Listing Agents'
  | 'Date Registered'
  | 'Pending Notifications'
  | 'Actions';

type ColumnIds =
  | 'address'
  | 'branch'
  | 'listing-agents'
  | 'date-registered'
  | 'pending-notifications'
  | 'actions';

const SORT_DIRECTIONS: SortDirection[] = ['asc', 'desc'];

const SORTABLE_COLUMNS: ColumnIds[] = ['address', 'date-registered', 'actions'];

interface OfferQueueProps {
  openContactDrawer: (
    mode: ContactDrawerMode,
    type: ContactDrawerType,
    id?: string,
  ) => void;
  reload: () => void;
  sortBy: ColumnIds;
  sortDirection: SortDirection;
  handleSort: (
    tab: 'offers',
    options: {
      sortBy: ColumnIds;
      sortDirection: SortDirection;
    },
  ) => void;
  listings: GetPendingRegisteredOffersListingsDTO;
  setOfferListing: (listing: PendingRegisteredOffersListing | null) => void;
  selectedListing?: PendingRegisteredOffersListing;
  getBoardName: (board: string, useOverride?: boolean) => string;
}

interface OfferQueueState {
  selectedListing: PendingRegisteredOffersListing | null;
  showForm801Modal: boolean;
  showPendingNotificationOffersModal: boolean;
  showManageOfferNotificationModal: boolean;
  isProcessing: boolean;
}

class OfferQueue extends React.Component<OfferQueueProps, OfferQueueState> {
  constructor(props: OfferQueueProps) {
    super(props);

    this.state = {
      selectedListing: null,
      showForm801Modal: false,
      showPendingNotificationOffersModal: false,
      showManageOfferNotificationModal: false,
      isProcessing: false,
    };
  }

  public componentDidMount() {
    const { selectedListing } = this.props;
    if (selectedListing) {
      this.setState({
        selectedListing,
        showManageOfferNotificationModal: true,
      });
    }
  }

  render() {
    const { listings, sortBy, sortDirection } = this.props;
    const {
      selectedListing,
      showForm801Modal,
      showPendingNotificationOffersModal,
      showManageOfferNotificationModal,
    } = this.state;

    if (listings.length === 0) {
      return <span>No listings with pending offers found</span>;
    }

    const COLUMNS = this.buildColumns();

    return (
      <div className="table-responsive">
        <Table striped bordered hover className="offer-queue">
          <thead>
            <tr>
              {COLUMNS.map(({ id, label, style }) => {
                const color = sortBy === id ? '#E91E63' : 'inherit';
                const sortable = SORTABLE_COLUMNS.includes(id);
                return (
                  <th
                    key={id}
                    style={{
                      ...style,
                      cursor: sortable ? 'pointer' : 'inherit',
                    }}
                    onClick={sortable ? () => this._handleSort(id) : undefined}
                  >
                    <div style={{ display: 'flex' }}>
                      <span
                        style={{
                          color,
                        }}
                      >
                        {label}
                      </span>
                      {sortable && (
                        <span
                          style={{ marginLeft: 'auto', marginRight: '1em' }}
                        >
                          <i
                            style={{
                              opacity: sortBy === id ? 1 : 0.5,
                              color,
                            }}
                            className={`fa fa-caret-${
                              sortDirection === 'asc' ? 'up' : 'down'
                            }`}
                          ></i>
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {listings.map(listing => (
              <tr
                data-cy={listing.id}
                key={`${listing.id}-${
                  listing.isPendingVerification
                    ? listing.pendingOffers[0].id
                    : 'pendingNotifications'
                }`}
                className="offer-queue__address-cell"
              >
                {COLUMNS.map(({ renderer }) => renderer(listing))}
              </tr>
            ))}
          </tbody>
        </Table>
        {selectedListing && (
          <Form801Modal
            data-cy="modal-popup"
            listing={selectedListing}
            offer={selectedListing.pendingOffers[0]}
            show={showForm801Modal}
            closeModal={this.closeForm801Modal}
          />
        )}
        {selectedListing && (
          <NewOffersModal
            listing={selectedListing}
            show={showPendingNotificationOffersModal}
            closeModal={this.closePendingNotificationOffersModal}
          />
        )}
        {selectedListing && (
          <ManageOfferNotificationModal
            listing={selectedListing}
            show={showManageOfferNotificationModal}
            closeModal={this.closeManageOfferNotificationModal}
          />
        )}
      </div>
    );
  }

  buildColumns = () => {
    const { isProcessing } = this.state;
    const { getBoardName, openContactDrawer } = this.props;

    const columns: Column<
      PendingRegisteredOffersListing,
      ColumnIds,
      ColumnLabels
    >[] = [
      {
        label: 'Address',
        id: 'address',
        renderer: data => (
          <td key={`address-${data.id}`}>
            <a href={`#/listing/${data.id}/view`}>{data.line1}</a>
            <ul>
              {data.mls_numbers &&
                data.mls_numbers.map(({ number, board }) => {
                  const formattedBoard = getBoardName(board);
                  return (
                    <li key={`${formattedBoard}-${number}`}>
                      {formattedBoard}: {number}
                    </li>
                  );
                })}
            </ul>
          </td>
        ),
      },
      {
        label: 'Branch',
        id: 'branch',
        renderer: data => (
          <td key={`branch-${data.id}`}>
            {data.org.locations[Number(data.location)].name}
          </td>
        ),
      },
      {
        label: 'Listing Agents',
        id: 'listing-agents',
        renderer: data => (
          <td
            key={`la-${data.id}`}
            className="offer-queue__listing-agents-cell"
          >
            {data.reps.map(({ firstName, lastName, id }) => (
              <div key={id}>
                <AgentChatLink
                  agentId={id}
                  firstName={firstName}
                  lastName={lastName}
                  openContactDrawer={openContactDrawer}
                />
              </div>
            ))}
          </td>
        ),
      },
      {
        label: 'Date Registered',
        id: 'date-registered',
        renderer: data => (
          <td key={`date-${data.id}`}>
            <div>
              {createFormatDateFunction('MMM dd, yyyy h:mm:ss a')(
                data.pendingOffers[0].createdAt,
                data.timezone,
              )}
            </div>
            <div>By: {data.pendingOffers[0].createdBy}</div>
          </td>
        ),
      },
      {
        label: 'Pending Notifications',
        id: 'pending-notifications',
        renderer: data => (
          <td key={`pending-${data.id}`}>
            {data.isPendingNotification && (
              <button
                className="btn btn-sm btn-link pending-notification-offers-btn"
                onClick={() => this.openPendingNotificationOffersModal(data)}
              >
                <div>
                  {data.pendingOffers.length} New Registration
                  {data.pendingOffers.length !== 1 ? 's' : ''}
                </div>
                {data.totalOfferCount && (
                  <div>{data.totalOfferCount} Offers Total</div>
                )}
              </button>
            )}
            {data.isPendingVerification && (
              <>
                <div>{data.pendingOffers[0].coop.name}</div>
                <div>{data.pendingOffers[0].coop.org.name}</div>
              </>
            )}
          </td>
        ),
      },
      {
        label: 'Actions',
        id: 'actions',
        renderer: data => (
          <td key={`actions-${data.id}`} className="offer-queue__action-cell">
            {data.isPendingNotification && (
              <button
                className="offer-queue__action-button btn btn-sm btn-primary"
                onClick={() => this.dismiss(data.id)}
                disabled={isProcessing}
              >
                Dismiss
              </button>
            )}
            {data.isPendingNotification && (
              <button
                className="offer-queue__action-button btn btn-sm btn-success"
                onClick={() => this.manageNotifications(data)}
                disabled={isProcessing}
              >
                Manage Notifications
              </button>
            )}
            {data.isPendingVerification && (
              <button
                className="offer-queue__action-button btn btn-sm btn-warning"
                onClick={() => this.openForm801Modal(data)}
                disabled={isProcessing}
              >
                Verify Offer Doc
              </button>
            )}
          </td>
        ),
      },
    ];
    return columns;
  };

  dismiss = (listingId: string) => {
    const { reload } = this.props;
    Swal.fire({
      title: 'Dismiss Offer Notification',
      text:
        'Are you sure you want to dismiss this offer notification alert and ' +
        'remove it from the queue? This may lead to some showing agents not ' +
        'being notified about an offer registration.',
      type: 'warning',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: 'YES, Dismiss',
      preConfirm: async () => {
        this.setState({ isProcessing: true });

        try {
          await axios.post(
            `/listing/${listingId}/offer/dismiss_pending_notification`,
          );
        } catch (err) {
          Swal.showValidationMessage(
            `Error: Failed to dismiss this item from the queue; ${err.message}`,
          );
        }
      },
    }).then(result => {
      this.setState({ isProcessing: false });
      if (result.value) {
        reload();
      }
    });
  };

  manageNotifications = (listing: PendingRegisteredOffersListing) => {
    this.openManageOfferNotificationModal(listing);
  };

  openForm801Modal = (listing: PendingRegisteredOffersListing) => {
    this.setState({
      selectedListing: listing,
      showForm801Modal: true,
    });
  };

  closeForm801Modal = (requireReload?: boolean, verified?: boolean) => {
    const { reload, setOfferListing } = this.props;
    const { selectedListing } = this.state;
    this.setState(
      {
        selectedListing: null,
        showForm801Modal: false,
      },
      () => {
        if (verified) setOfferListing(selectedListing);
        if (requireReload) reload();
      },
    );
  };

  openManageOfferNotificationModal = (
    listing: PendingRegisteredOffersListing,
  ) => {
    this.setState({
      selectedListing: listing,
      showManageOfferNotificationModal: true,
    });
  };

  closeManageOfferNotificationModal = (requireReload?: boolean) => {
    const { reload, setOfferListing } = this.props;
    this.setState({
      selectedListing: null,
      showManageOfferNotificationModal: false,
    });
    setOfferListing(null);
    if (requireReload) reload();
  };

  openPendingNotificationOffersModal = (
    listing: PendingRegisteredOffersListing,
  ) => {
    this.setState({
      selectedListing: listing,
      showPendingNotificationOffersModal: true,
    });
  };

  closePendingNotificationOffersModal = () => {
    this.setState({
      selectedListing: null,
      showPendingNotificationOffersModal: false,
    });
  };

  _handleSort = (columnId: ColumnIds) => {
    const { sortBy, sortDirection, handleSort } = this.props;
    const currentSort = SORT_DIRECTIONS.indexOf(sortDirection);

    handleSort('offers', {
      sortBy: columnId,
      sortDirection:
        sortBy === columnId ? SORT_DIRECTIONS[1 - currentSort] : 'desc',
    });
  };
}

export default OfferQueue;
